/**
 * AdminPairPage - Admin genehmigt Station-Pairing
 *
 * Auth Required: Admin + 2FA
 * URL: /admin/pair?token=...
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Monitor, Check, AlertTriangle, Loader2, ArrowLeft, MapPin } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { adminApprovePairing, ApiException } from '../../api/client';

type StationType = 'WSA' | 'VORARBEITER' | 'ADMIN';

type PageState = 'form' | 'loading' | 'success' | 'error';

const TYPE_OPTIONS: { value: StationType; label: string; description: string }[] = [
  { value: 'WSA', label: 'WSA-Erfassung', description: 'Waschmaschinen-Erfassung fur Mitarbeiter' },
  { value: 'VORARBEITER', label: 'Vorarbeiter', description: 'Vorarbeiter-Station mit erweiterten Funktionen' },
  { value: 'ADMIN', label: 'Admin', description: 'Administrations-Terminal' },
];

export default function AdminPairPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState<PageState>('form');
  const [type, setType] = useState<StationType>('WSA');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [stationId, setStationId] = useState<string | null>(null);

  // Token validieren (64 hex chars erwartet)
  const isTokenValid = token && token.length === 64 && /^[a-f0-9]+$/i.test(token);

  useEffect(() => {
    if (!token) {
      setError('Ungueltiger Link: Kein Pairing-Token angegeben');
      setState('error');
    } else if (!isTokenValid) {
      setError('Ungueltiger Link: Token ist ungueltig oder unvollstaendig');
      setState('error');
    }
  }, [token, isTokenValid]);

  // Pairing genehmigen
  const handleApprove = async () => {
    if (!token) return;

    setState('loading');
    setError(null);

    try {
      const result = await adminApprovePairing(
        token,
        type,
        name.trim() || undefined,
        location.trim() || undefined,
      );

      setStationId(result.stationId);
      setState('success');
    } catch (err) {
      if (err instanceof ApiException) {
        switch (err.data.code) {
          case 'PAIRING_NOT_FOUND':
            setError('Pairing-Code ungultig oder nicht gefunden');
            break;
          case 'PAIRING_EXPIRED':
            setError('Pairing-Code ist abgelaufen');
            break;
          case 'PAIRING_ALREADY_CLAIMED':
            setError('Dieses Pairing wurde bereits genehmigt');
            break;
          case 'TWO_FACTOR_REQUIRED':
            setError('2FA-Verifizierung erforderlich. Bitte zuerst 2FA bestatigen.');
            break;
          case 'UNAUTHORIZED':
          case 'SESSION_EXPIRED':
            setError('Sitzung abgelaufen. Bitte erneut anmelden.');
            break;
          case 'FORBIDDEN':
            setError('Keine Berechtigung fur diese Aktion');
            break;
          default:
            setError(err.data.error || 'Fehler beim Genehmigen');
        }
      } else {
        setError('Verbindungsfehler');
      }
      setState('error');
    }
  };

  // Token-Kurzform (erste 8 Zeichen) - nur zur visuellen Identifikation
  const tokenShort = token ? token.substring(0, 8).toUpperCase() : null;

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin/stations"
            className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zuruck zu Stationen
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Monitor className="w-6 h-6" />
            Station koppeln
          </h1>
        </div>

        {/* Form State */}
        {state === 'form' && isTokenValid && (
          <div className="bg-white rounded-lg border shadow-sm">
            {/* Pairing Info */}
            <div className="p-6 border-b bg-slate-50">
              <p className="text-sm text-slate-600 mb-1">Token (Kurzform)</p>
              <p className="text-2xl font-mono font-bold text-slate-900 tracking-widest">
                {tokenShort}
              </p>
              <p className="text-xs text-slate-400 mt-2">
                Ein Geraet wartet auf Genehmigung fuer diesen Token.
              </p>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6">
              {/* Typ */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Stationstyp
                </label>
                <div className="space-y-2">
                  {TYPE_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        type === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value={option.value}
                        checked={type === option.value}
                        onChange={(e) => setType(e.target.value as StationType)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-slate-900">{option.label}</p>
                        <p className="text-sm text-slate-500">{option.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. WSA-Terminal 1"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Standort */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Standort (optional)
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="z.B. Halle A"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Submit */}
              <button
                onClick={handleApprove}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Pairing genehmigen
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {state === 'loading' && (
          <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
            <p className="text-slate-600 mt-4">Pairing wird genehmigt...</p>
          </div>
        )}

        {/* Success State */}
        {state === 'success' && (
          <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-green-700 mb-2">Station erfolgreich gekoppelt</h2>
            <p className="text-slate-600 mb-6">
              Die Station wurde registriert und kann jetzt verwendet werden.
            </p>
            <div className="flex gap-3 justify-center">
              {stationId && (
                <Link
                  to={`/admin/stations/${stationId}`}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  Zur Station
                </Link>
              )}
              <Link
                to="/admin/stations"
                className="px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg font-medium transition-colors"
              >
                Alle Stationen
              </Link>
            </div>
          </div>
        )}

        {/* Error State */}
        {state === 'error' && (
          <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-red-700 mb-2">
              {!token || !isTokenValid ? 'Ungueltiger Link' : 'Fehler'}
            </h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              {isTokenValid && (
                <button
                  onClick={() => setState('form')}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium transition-colors"
                >
                  Erneut versuchen
                </button>
              )}
              <Link
                to="/admin/stations"
                className="px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg font-medium transition-colors"
              >
                Zuruck
              </Link>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
